/**
 * Tableau des agents avec métriques de performance
 * Composant pour afficher les données des agents dans le dashboard de monitoring
 * @author Cascade (Tech Lead)
 * @date 2025-07-09
 */

import React, { useState, useMemo } from 'react';
import type { AgentsTableProps } from '../../../types/monitoring.types';
import type { MetricsAgent } from '../../../types/monitoring.types';

const AgentsTable: React.FC<AgentsTableProps> = ({
  agents,
  isLoading = false,
  onAgentClick,
  sortBy = 'name',
  sortOrder = 'asc',
  className = ''
}) => {
  const [currentSortBy, setCurrentSortBy] = useState(sortBy);
  const [currentSortOrder, setCurrentSortOrder] = useState<'asc' | 'desc'>(sortOrder);

  // Fonction de tri
  const sortedAgents = useMemo(() => {
    if (!agents) return [];

    return [...agents].sort((a, b) => {
      let aValue: string | number;
      let bValue: string | number;

      switch (currentSortBy) {
        case 'name':
          aValue = a.agent_name.toLowerCase();
          bValue = b.agent_name.toLowerCase();
          break;
        case 'conversations':
          aValue = a.conversations_count;
          bValue = b.conversations_count;
          break;
        case 'cost':
          aValue = a.cost_usd;
          bValue = b.cost_usd;
          break;
        case 'response_time':
          aValue = a.avg_response_time_ms;
          bValue = b.avg_response_time_ms;
          break;
        default:
          aValue = a.agent_name.toLowerCase();
          bValue = b.agent_name.toLowerCase();
      }

      if (aValue < bValue) {
        return currentSortOrder === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return currentSortOrder === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [agents, currentSortBy, currentSortOrder]);

  // Gestion du tri
  const handleSort = (field: typeof currentSortBy) => {
    if (field === currentSortBy) {
      setCurrentSortOrder(currentSortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setCurrentSortBy(field);
      setCurrentSortOrder('asc');
    }
  };

  // Composant de loading
  if (isLoading) {
    return (
      <div className={`bg-white rounded-lg shadow-md border border-gray-200 ${className}`}>
        <div className="p-6">
          <div className="animate-pulse">
            <div className="h-6 bg-gray-300 rounded w-1/4 mb-4"></div>
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex space-x-4">
                  <div className="h-4 bg-gray-300 rounded flex-1"></div>
                  <div className="h-4 bg-gray-300 rounded w-20"></div>
                  <div className="h-4 bg-gray-300 rounded w-20"></div>
                  <div className="h-4 bg-gray-300 rounded w-24"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Fonction pour obtenir la couleur du statut temps de réponse
  const getResponseTimeColor = (responseTime: number): string => {
    if (responseTime > 1000) return 'text-red-600 bg-red-50';
    if (responseTime > 500) return 'text-yellow-600 bg-yellow-50';
    return 'text-green-600 bg-green-50';
  };

  // Fonction pour obtenir l'icône de tri
  const getSortIcon = (field: typeof currentSortBy): string => {
    if (field !== currentSortBy) return '↕';
    return currentSortOrder === 'asc' ? '↑' : '↓';
  };

  return (
    <div className={`bg-white rounded-lg shadow-md border border-gray-200 ${className}`}>
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900">Agents Performance</h3>
        <p className="text-sm text-gray-600">Métriques détaillées par agent</p>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('name')}
              >
                <div className="flex items-center space-x-1">
                  <span>Agent</span>
                  <span className="text-gray-400">{getSortIcon('name')}</span>
                </div>
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('conversations')}
              >
                <div className="flex items-center space-x-1">
                  <span>Conversations</span>
                  <span className="text-gray-400">{getSortIcon('conversations')}</span>
                </div>
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('cost')}
              >
                <div className="flex items-center space-x-1">
                  <span>Coût</span>
                  <span className="text-gray-400">{getSortIcon('cost')}</span>
                </div>
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('response_time')}
              >
                <div className="flex items-center space-x-1">
                  <span>Temps de réponse</span>
                  <span className="text-gray-400">{getSortIcon('response_time')}</span>
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedAgents.map((agent) => (
              <tr 
                key={agent.agent_id} 
                className="hover:bg-gray-50 cursor-pointer transition-colors duration-150"
                onClick={() => onAgentClick?.(agent.agent_id)}
              >
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10">
                      <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                        <span className="text-sm font-medium text-blue-600">
                          {agent.agent_name.substring(0, 2).toUpperCase()}
                        </span>
                      </div>
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900">
                        {agent.agent_name}
                      </div>
                      <div className="text-sm text-gray-500">
                        ID: {agent.agent_id}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">
                    {agent.conversations_count.toLocaleString()}
                  </div>
                  <div className="text-sm text-gray-500">
                    conversations
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">
                    ${agent.cost_usd.toFixed(2)}
                  </div>
                  <div className="text-sm text-gray-500">
                    USD
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getResponseTimeColor(agent.avg_response_time_ms)}`}>
                    {agent.avg_response_time_ms}ms
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onAgentClick?.(agent.agent_id);
                    }}
                    className="text-blue-600 hover:text-blue-900 mr-3"
                  >
                    Voir détails
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      // Action de configuration
                    }}
                    className="text-gray-600 hover:text-gray-900"
                  >
                    Configurer
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer avec résumé */}
      <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
        <div className="flex items-center justify-between text-sm text-gray-600">
          <div>
            Total: {agents.length} agent{agents.length > 1 ? 's' : ''}
          </div>
          <div className="flex space-x-4">
            <span>
              Conversations totales: {agents.reduce((sum, agent) => sum + agent.conversations_count, 0).toLocaleString()}
            </span>
            <span>
              Coût total: ${agents.reduce((sum, agent) => sum + agent.cost_usd, 0).toFixed(2)}
            </span>
            <span>
              Temps moyen: {Math.round(agents.reduce((sum, agent) => sum + agent.avg_response_time_ms, 0) / agents.length)}ms
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentsTable;
